import { useEffect } from 'react';
import { ProgressShareData } from '@/api/share';
import { useProgressShareImage } from '@/hooks/useShareImage';

interface ProgressShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: ProgressShareData | null;
}

/**
 * ProgressShareModal
 * Displays after successful activity sync, encouraging users to share their progress.
 * Pre-generates the share image while the modal is displayed.
 */
export function ProgressShareModal({
  isOpen,
  onClose,
  data,
}: ProgressShareModalProps) {
  const { share, download, isReady, isGenerating, isSharing } =
    useProgressShareImage(isOpen ? data : null);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !data) return null;

  const handleShare = async () => {
    await share();
  };

  const handleDownload = async () => {
    await download();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="relative flex w-full max-w-sm flex-col bg-[#0c1c0c] border border-primary/20 rounded-3xl p-6 shadow-xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <span className="material-symbols-outlined text-white/60">close</span>
        </button>

        {/* Success Icon */}
        <div className="flex justify-center mb-4">
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-primary/20">
            <span className="material-symbols-outlined text-primary text-5xl">
              fitness_center
            </span>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-white text-2xl font-bold text-center mb-2">
          Activity Logged!
        </h2>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <div className="text-primary text-xl font-bold">
              {data.distance.toFixed(2)} km
            </div>
            <div className="text-white/50 text-xs">Distance</div>
          </div>
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <div className="text-primary text-xl font-bold">{data.calories}</div>
            <div className="text-white/50 text-xs">Calories</div>
          </div>
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <div className="text-primary text-xl font-bold">#{data.rank}</div>
            <div className="text-white/50 text-xs">Rank</div>
          </div>
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <div className="text-primary text-xl font-bold">
              {data.daysCompleted}/{data.totalDays}
            </div>
            <div className="text-white/50 text-xs">Days</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-white/50 mb-1">
            <span>Challenge Progress</span>
            <span>{Math.round(data.completionPercentage)}%</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${data.completionPercentage}%` }}
            />
          </div>
        </div>

        {/* Description */}
        <p className="text-white/60 text-sm text-center mb-6">
          Share your progress and inspire others to join the challenge!
        </p>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          {/* Share Button - Primary CTA */}
          <button
            onClick={handleShare}
            disabled={isSharing || isGenerating}
            className={`w-full h-14 rounded-full font-bold text-lg flex items-center justify-center gap-2 transition-all ${
              isSharing || isGenerating
                ? 'bg-primary/70 text-[#0c1c0c]/70 cursor-not-allowed'
                : 'bg-primary text-[#0c1c0c] shadow-[0_0_20px_-5px_rgba(37,244,120,0.5)] active:scale-[0.98]'
            }`}
          >
            {isSharing ? (
              <>
                <span className="material-symbols-outlined animate-spin">
                  sync
                </span>
                Sharing...
              </>
            ) : isGenerating ? (
              <>
                <span className="material-symbols-outlined animate-spin">
                  hourglass_empty
                </span>
                Preparing...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined">ios_share</span>
                Share Progress
              </>
            )}
          </button>

          {/* Download Button - Secondary */}
          <button
            onClick={handleDownload}
            disabled={isSharing || isGenerating}
            className={`w-full h-12 rounded-full border font-semibold flex items-center justify-center gap-2 transition-colors active:scale-[0.98] ${
              isSharing || isGenerating
                ? 'border-white/10 text-white/40 cursor-not-allowed'
                : 'border-white/20 text-white/80 hover:bg-white/5'
            }`}
          >
            <span className="material-symbols-outlined text-xl">download</span>
            Save Image
          </button>

          {/* Skip Button */}
          <button
            onClick={onClose}
            className="w-full h-10 text-white/50 text-sm font-medium hover:text-white/70 transition-colors"
          >
            Maybe Later
          </button>
        </div>

        {/* Image Ready Indicator */}
        {isReady && !isSharing && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
            <span className="text-primary/60 text-xs flex items-center gap-1">
              <span className="material-symbols-outlined text-sm">check_circle</span>
              Image ready
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
